import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useIsMobile from '../hooks/useIsMobile';
import { Button, Icon, Logo } from '../ui';
import { IconName } from '../ui/iconSprite';
import ThemeToggle from '../ui/ThemeToggle';

/** /how — оршин суугчид зориулсан «QR хэрхэн ажилладаг вэ?» товч хөдөлгөөнт дүрслэл.
 *  Утасны хүрээнд экран автоматаар алхам алхмаар солигдоно; Дараах/Өмнөх, цэг, зогсоох.
 *  Backend хэрэггүй — бүх экран статик mock. Дизайн систем: tokens.css / ui primitives. */

const STEP_MS = 4200;

type Step = { icon: IconName; title: string; desc: string; screen: ScreenKey };
type ScreenKey = 'door' | 'scan' | 'balance' | 'pay' | 'success' | 'feedback' | 'more';

const STEPS: Step[] = [
  { icon: 'door', title: 'Хаалганы QR карт', desc: 'СӨХ айл бүрд өөрийн QR карт хэвлэж хаалганд наана. Карт дээр СӨХ-ийн нэр, байр, тоот бичээстэй.', screen: 'door' },
  { icon: 'scan', title: 'Утсаараа уншуулна', desc: 'Утасны камерыг QR-т чиглүүлнэ. Апп татах, бүртгүүлэх, нууц үг — юу ч хэрэггүй.', screen: 'scan' },
  { icon: 'wallet', title: 'Үлдэгдлээ харна', desc: 'Тоотын нэр, төлөх үлдэгдэл, нэхэмжлэхийн задаргаа шууд гарч ирнэ.', screen: 'balance' },
  { icon: 'qr', title: 'QPay-ээр төлнө', desc: '«QPay-ээр төлөх» дарж банкны аппаа сонгоно, эсвэл QR-ийг банкны аппаар уншуулна.', screen: 'pay' },
  { icon: 'check-circle', title: 'Төлбөр амжилттай', desc: 'Төлбөр орсон даруйд үлдэгдэл 0 болно. Баримтаа хадгалж авч болно.', screen: 'success' },
  { icon: 'message', title: 'Санал хүсэлт · Холбоо барих', desc: 'Гомдол, саналаа тоотоо бичэлгүй илгээнэ. СӨХ-ийн захирал, ажилтнуудын утас нэг дороос.', screen: 'feedback' },
  { icon: 'car', title: 'Автомашин · Тайлан', desc: 'Өөрийн тоотод машинаа бүртгүүлж, СӨХ-ийн нийтэлсэн санхүүгийн тайланг үзнэ.', screen: 'more' },
];

const CSS = `
@keyframes how-in { from { opacity:0; transform:translateY(10px) } to { opacity:1; transform:none } }
@keyframes how-bar { from { width:0 } to { width:100% } }
@keyframes how-scan { 0% { top:6% } 50% { top:88% } 100% { top:6% } }
@keyframes how-pulse { 0%,100% { transform:scale(1); opacity:.9 } 50% { transform:scale(1.06); opacity:1 } }
@keyframes how-pop { 0% { transform:scale(.4); opacity:0 } 70% { transform:scale(1.1); opacity:1 } 100% { transform:scale(1) } }
@keyframes how-tap { 0%,60% { transform:scale(1) } 70% { transform:scale(.96) } 80%,100% { transform:scale(1) } }
@keyframes how-count { from { opacity:0; transform:translateY(6px) } to { opacity:1; transform:none } }
.how-screen { animation: how-in .45s ease-out both }
.how-tap { animation: how-tap 2.4s ease-in-out infinite }
@media (prefers-reduced-motion: reduce) { .how-screen, .how-tap, .how-anim { animation: none !important } }
`;

export default function HowItWorksPage() {
  const navigate = useNavigate();
  const m = useIsMobile(860);
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [tick, setTick] = useState(0); // progress bar-ыг дахин эхлүүлэх түлхүүр
  const timer = useRef<any>(null);

  useEffect(() => {
    if (!playing) return;
    timer.current = setTimeout(() => { setI(v => (v + 1) % STEPS.length); setTick(t => t + 1); }, STEP_MS);
    return () => clearTimeout(timer.current);
  }, [i, playing, tick]);

  const go = (n: number, manual = true) => { setI((n + STEPS.length) % STEPS.length); setTick(t => t + 1); if (manual) setPlaying(false); };
  const step = STEPS[i];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--ink)' }}>
      <style>{CSS}</style>
      <header style={{ background: 'var(--card)', borderBottom: '1px solid var(--line)', padding: m ? '12px 16px' : '14px 20px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => navigate('/welcome')} className="sh-btn sh-btn--ghost" style={{ minHeight: 40, padding: '0 8px', gap: 8 }}><Logo size={32} /><span style={{ fontWeight: 700, fontSize: 16 }}>Сайн Хөрш</span></button>
          <span style={{ flex: 1 }} />
          <ThemeToggle size={40} />
          {!m && <Button variant="secondary" size="sm" style={{ minHeight: 40 }} icon="phone" onClick={() => navigate('/u/demo')}>Жишээ хуудас</Button>}
        </div>
      </header>

      <main style={{ maxWidth: 1100, margin: '0 auto', padding: m ? '24px 16px 40px' : '48px 40px 64px' }}>
        <div style={{ textAlign: 'center', marginBottom: m ? 20 : 36 }}>
          <span className="sh-chip sh-chip--paid"><Icon name="qr" size={14} />Оршин суугчид зориулсан</span>
          <h1 style={{ fontSize: m ? 28 : 40, fontWeight: 700, letterSpacing: '-.02em', lineHeight: 1.1, margin: '14px 0 8px' }}>QR хэрхэн ажилладаг вэ?</h1>
          <p style={{ color: 'var(--muted)', fontSize: m ? 15 : 17, margin: 0 }}>Уншуулахаас төлөх хүртэл — 30 секунд, 7 алхам.</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: m ? '1fr' : '340px 1fr', gap: m ? 20 : 48, alignItems: 'center' }}>
          {/* Утас */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            <Phone>
              <Screen key={`${step.screen}-${tick}`} kind={step.screen} />
            </Phone>
            {/* Progress + удирдлага */}
            <div style={{ width: 280, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', gap: 4 }}>
                {STEPS.map((_, k) => (
                  <button key={k} onClick={() => go(k)} aria-label={`${k + 1}-р алхам`} style={{ flex: 1, height: 6, borderRadius: 3, border: 0, padding: 0, cursor: 'pointer', background: k < i ? 'var(--green)' : 'var(--line)', position: 'relative', overflow: 'hidden' }}>
                    {k === i && <span key={tick} className="how-anim" style={{ position: 'absolute', inset: 0, background: 'var(--green)', width: playing ? undefined : '100%', animation: playing ? `how-bar ${STEP_MS}ms linear forwards` : 'none' }} />}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <button className="sh-btn sh-btn--ghost" style={{ minHeight: 40, width: 40, padding: 0 }} onClick={() => go(i - 1)} aria-label="Өмнөх"><Icon name="arrow-left" size={18} /></button>
                <button className="sh-btn sh-btn--secondary" style={{ minHeight: 40, padding: '0 14px' }} onClick={() => { setPlaying(p => !p); setTick(t => t + 1); }}>
                  {playing ? 'Зогсоох' : 'Үргэлжлүүлэх'}
                </button>
                <button className="sh-btn sh-btn--ghost" style={{ minHeight: 40, width: 40, padding: 0 }} onClick={() => go(i + 1)} aria-label="Дараах"><Icon name="arrow-right" size={18} /></button>
              </div>
            </div>
          </div>

          {/* Алхмууд */}
          <div>
            {m ? (
              <div key={i} className="sh-card how-screen" style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <StepIcon icon={step.icon} active />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--green)', letterSpacing: '.06em' }}>{i + 1} / {STEPS.length}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, margin: '2px 0 4px' }}>{step.title}</div>
                  <div style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.5 }}>{step.desc}</div>
                </div>
              </div>
            ) : (
              <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {STEPS.map((s, k) => {
                  const on = k === i;
                  return (
                    <li key={k}>
                      <button onClick={() => go(k)} style={{ width: '100%', textAlign: 'left', display: 'flex', gap: 14, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 'var(--r-md)', border: `1px solid ${on ? 'var(--green)' : 'transparent'}`, background: on ? 'var(--card)' : 'transparent', cursor: 'pointer', color: 'var(--ink)', transition: 'all .25s', boxShadow: on ? 'var(--shadow)' : 'none' }}>
                        <StepIcon icon={s.icon} active={on} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 16, fontWeight: 700, color: on ? 'var(--ink)' : 'var(--muted)' }}>{k + 1}. {s.title}</div>
                          <div style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.5, maxHeight: on ? 80 : 0, overflow: 'hidden', opacity: on ? 1 : 0, transition: 'all .3s', marginTop: on ? 4 : 0 }}>{s.desc}</div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ol>
            )}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 22 }}>
              <Button style={{ minHeight: 48 }} block={m} icon="phone" onClick={() => navigate('/u/demo')}>Жишээ хуудсыг өөрөө үзэх</Button>
              <Button variant="secondary" style={{ minHeight: 48 }} block={m} onClick={() => navigate('/welcome')}>Нүүр хуудас</Button>
            </div>
          </div>
        </div>
      </main>
      <footer style={{ padding: '24px 20px', textAlign: 'center', fontSize: 12, color: 'var(--muted)', lineHeight: 1.8 }}><div>© 2026 Сайн Хөрш · Улаанбаатар · nationalmobile976@gmail.com</div><div>Харилцах утас: <a href="tel:96900520" style={{ color: 'inherit', fontWeight: 600, textDecoration: 'none' }}>96900520</a></div></footer>
    </div>
  );
}

function StepIcon({ icon, active }: { icon: IconName; active: boolean }) {
  return (
    <span style={{ width: 44, height: 44, borderRadius: '50%', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: active ? 'var(--green)' : 'var(--green-soft)', color: active ? '#fff' : 'var(--green)', transition: 'all .25s' }}>
      <Icon name={icon} size={22} />
    </span>
  );
}

/* ── Утасны хүрээ ── */
function Phone({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ width: 280, height: 560, background: '#1c2b22', borderRadius: 40, padding: 10, boxShadow: '0 24px 48px rgba(0,0,0,.28)', position: 'relative', flexShrink: 0 }}>
      <div style={{ position: 'absolute', top: 18, left: '50%', transform: 'translateX(-50%)', width: 84, height: 22, background: '#1c2b22', borderRadius: 12, zIndex: 3 }} />
      <div style={{ background: '#f4f1e8', color: '#1c2b22', borderRadius: 31, height: '100%', overflow: 'hidden', position: 'relative' }}>
        {children}
      </div>
    </div>
  );
}

/* ── Экранууд (статик mock, PublicUnitPage-ийн харагдацтай ижил) ── */
const G = '#236844', G2 = '#2f7d52', RED = '#b3261e', MUTED = '#5c6a61', LINE = '#e3e0d6', CARD = '#fbfaf6', GOLD = '#d9a441';
const P = { padding: '52px 16px 16px', display: 'flex', flexDirection: 'column' as const, gap: 12, height: '100%', boxSizing: 'border-box' as const };

function Screen({ kind }: { kind: ScreenKey }) {
  switch (kind) {
    case 'door': return <DoorScreen />;
    case 'scan': return <ScanScreen />;
    case 'balance': return <BalanceScreen highlight="pay" />;
    case 'pay': return <PayScreen />;
    case 'success': return <SuccessScreen />;
    case 'feedback': return <BalanceScreen highlight="tiles1" paid />;
    case 'more': return <BalanceScreen highlight="tiles2" paid />;
  }
}

function MiniHeader({ unit = 'А байр, 101 тоот' }: { unit?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ width: 34, height: 34, borderRadius: 10, background: `linear-gradient(135deg,${G2},${G})`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: GOLD }}><Icon name="home" size={18} /></span>
      <div><div style={{ fontSize: 11, color: MUTED }}>Нарны Хороолол СӨХ</div><div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.1 }}>{unit}</div></div>
    </div>
  );
}
function Tile({ icon, label, hot }: { icon: IconName; label: string; hot?: boolean }) {
  return (
    <div className={hot ? 'how-tap' : undefined} style={{ background: CARD, border: `1.5px solid ${hot ? G : LINE}`, borderRadius: 12, minHeight: 56, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, padding: 8, boxShadow: hot ? `0 0 0 4px rgba(47,125,82,.18)` : 'none' }}>
      <Icon name={icon} size={18} style={{ color: G }} /><span style={{ fontSize: 11, fontWeight: 600, textAlign: 'center', lineHeight: 1.15 }}>{label}</span>
    </div>
  );
}

function DoorScreen() {
  // Хаалганд наасан бодит QR карт — printQr.ts-ийн A6 картын загвартай яг ижил
  return (
    <div className="how-screen" style={{ height: '100%', background: 'linear-gradient(#e9e4d6,#d9d3c2)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '44px 10px 10px' }}>
      <div style={{ animation: 'how-pulse 3s ease-in-out infinite' }}><QrCardMock scale={.86} /></div>
    </div>
  );
}

/** QR карт (хэвлэх загвар): ногоон судал, лого + СӨХ, «Төлбөр · Санал хүсэлт · Холбоо», QR, тоот, 3 заавар, захирлын утас. */
function QrCardMock({ scale = 1, soh = 'Нарны Хороолол СӨХ', unit = 'А байр · 101 тоот', phone = '9911 2233' }: { scale?: number; soh?: string; unit?: string; phone?: string }) {
  const W = 270;
  return (
    <div style={{ width: W, flexShrink: 0, transform: `scale(${scale})`, transformOrigin: 'center', position: 'relative', overflow: 'hidden', background: '#fbfaf6', color: '#1c2b22', border: '1px solid #e8e5dc', borderRadius: 12, padding: '22px 16px 14px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, boxShadow: '0 14px 30px rgba(0,0,0,.22)' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 8, background: `linear-gradient(135deg,${G2},${G})` }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left' }}>
        <span style={{ width: 40, height: 40, borderRadius: 10, background: `linear-gradient(135deg,${G2},${G})`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#ffd166', flexShrink: 0 }}><Icon name="home" size={20} /></span>
        <div><div style={{ fontSize: 10, color: MUTED, letterSpacing: '.06em' }}>СӨХ</div><div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.15 }}>{soh}</div></div>
      </div>
      <div style={{ fontSize: 11, color: MUTED }}>Төлбөр · Санал хүсэлт · Холбоо</div>
      <div style={{ background: '#fff', padding: 10, borderRadius: 10, border: '1px solid #e8e5dc', lineHeight: 0 }}><QrMock size={150} n={21} /></div>
      <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-.01em' }}>{unit}</div>
      <div style={{ display: 'flex', gap: 18, justifyContent: 'center' }}>
        {([['scan', 'Утсаар\nуншуул'], ['wallet', 'QPay төлбөр'], ['message', 'Санал хүсэлт']] as [IconName, string][]).map(([ic, t]) => (
          <div key={ic} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, fontSize: 10, lineHeight: 1.2, minWidth: 60, whiteSpace: 'pre-line' }}><Icon name={ic} size={20} style={{ color: G }} />{t}</div>
        ))}
      </div>
      <div style={{ fontSize: 10, color: MUTED, display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}><Icon name="call" size={11} />СӨХ-ийн захирал: <b style={{ color: '#1c2b22' }}>{phone}</b></div>
    </div>
  );
}
function ScanScreen() {
  return (
    <div className="how-screen" style={{ height: '100%', background: '#0c1410', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', top: 60, left: 0, right: 0, textAlign: 'center', color: '#fff', fontSize: 12, opacity: .8 }}>Камер</div>
      <div style={{ width: 200, height: 200, position: 'relative' }}>
        {[['top', 'left'], ['top', 'right'], ['bottom', 'left'], ['bottom', 'right']].map(([v, h]) => (
          <span key={v + h} style={{ position: 'absolute', [v]: 0, [h]: 0, width: 28, height: 28, [`border${v[0].toUpperCase() + v.slice(1)}`]: '4px solid #fff', [`border${h[0].toUpperCase() + h.slice(1)}`]: '4px solid #fff', borderRadius: 4 } as any} />
        ))}
        <div style={{ position: 'absolute', inset: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', borderRadius: 8 }}>
          <QrCardMock scale={.62} />
        </div>
        <div className="how-anim" style={{ position: 'absolute', left: 8, right: 8, height: 3, background: '#6ee7a0', boxShadow: '0 0 12px #6ee7a0', borderRadius: 2, animation: 'how-scan 2.2s ease-in-out infinite' }} />
      </div>
      <div style={{ position: 'absolute', bottom: 40, left: 20, right: 20, background: 'rgba(255,255,255,.95)', color: '#1c2b22', borderRadius: 12, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, animation: 'how-in .5s 1.2s ease-out both' }}>
        <Icon name="qr" size={18} style={{ color: G }} /><div><div style={{ fontWeight: 700 }}>sainhursh.mn/u/…</div><div style={{ color: MUTED, fontSize: 11 }}>Нээх</div></div>
      </div>
    </div>
  );
}
function BalanceScreen({ highlight, paid }: { highlight: 'pay' | 'tiles1' | 'tiles2'; paid?: boolean }) {
  return (
    <div className="how-screen" style={P}>
      <MiniHeader />
      <div style={{ background: CARD, border: `1px solid ${LINE}`, borderRadius: 14, padding: '14px 12px', textAlign: 'center' }}>
        <div style={{ fontSize: 11, color: MUTED }}>Төлөх үлдэгдэл</div>
        {paid ? (
          <><div style={{ fontSize: 28, fontWeight: 800, color: G2, margin: '4px 0 2px' }}>0 ₮</div><div style={{ fontSize: 11, color: MUTED }}>Бүх төлбөр төлөгдсөн</div></>
        ) : (
          <>
            <div style={{ fontSize: 28, fontWeight: 800, color: RED, margin: '4px 0 2px', animation: 'how-count .5s .2s ease-out both' }}>186 500 ₮</div>
            <div style={{ fontSize: 11, color: MUTED }}>Хугацаа: 2026.09.15 · 3 нэхэмжлэх</div>
            <div className={highlight === 'pay' ? 'how-tap' : undefined} style={{ marginTop: 10, background: `linear-gradient(135deg,${G2},${G})`, color: '#fff', borderRadius: 10, padding: '11px 0', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, boxShadow: highlight === 'pay' ? '0 0 0 4px rgba(47,125,82,.22)' : 'none' }}><Icon name="wallet" size={16} />QPay-ээр төлөх</div>
          </>
        )}
      </div>
      {!paid && (
        <div style={{ background: CARD, border: `1px solid ${LINE}`, borderRadius: 14, fontSize: 11 }}>
          {[['СӨХ хураамж · 8-р сар', '68 500 ₮'], ['СӨХ хураамж · 7-р сар', '68 500 ₮'], ['Лифтний засвар', '49 500 ₮']].map(([t, a], k) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', borderTop: k ? `1px solid ${LINE}` : 'none' }}><span>{t}</span><b>{a}</b></div>
          ))}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 'auto' }}>
        <Tile icon="message" label="Санал хүсэлт" hot={highlight === 'tiles1'} />
        <Tile icon="call" label="Холбоо барих" hot={highlight === 'tiles1'} />
        <Tile icon="car" label="Автомашин бүртгүүлэх" hot={highlight === 'tiles2'} />
        <Tile icon="file" label="Тайлан" hot={highlight === 'tiles2'} />
      </div>
    </div>
  );
}
function PayScreen() {
  const banks = ['Хаан', 'Голомт', 'ХХБ', 'Төрийн', 'Хас', 'MonPay'];
  return (
    <div className="how-screen" style={P}>
      <div style={{ fontSize: 12, color: G, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}><Icon name="arrow-left" size={14} />Буцах</div>
      <div style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 14, padding: 12, textAlign: 'center' }}>
        <div style={{ fontSize: 11, color: MUTED }}>Төлөх дүн</div>
        <div style={{ fontSize: 22, fontWeight: 800, margin: '2px 0 8px' }}>186 500 ₮</div>
        <QrMock size={120} />
        <div style={{ fontSize: 10, color: MUTED, marginTop: 6 }}>Банкны аппаараа QR уншуулна уу</div>
      </div>
      <div style={{ fontSize: 11, fontWeight: 600, color: MUTED, textAlign: 'center' }}>Эсвэл аппаа сонгож шууд төлөх</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
        {banks.map((b, k) => (
          <div key={b} className={k === 0 ? 'how-tap' : undefined} style={{ background: CARD, border: `1.5px solid ${k === 0 ? G : LINE}`, borderRadius: 10, padding: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, animation: `how-in .4s ${.15 + k * .07}s ease-out both` }}>
            <span style={{ width: 26, height: 26, borderRadius: 7, background: k === 0 ? G : '#e8efe9', color: k === 0 ? '#fff' : G, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800 }}>{b.slice(0, 2)}</span>
            <span style={{ fontSize: 9, fontWeight: 600 }}>{b}</span>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 10, color: MUTED, marginTop: 'auto' }}><span className="sh-spinner" style={{ width: 12, height: 12 }} />Төлбөр хүлээж байна…</div>
    </div>
  );
}
function SuccessScreen() {
  return (
    <div className="how-screen" style={{ height: '100%', background: `linear-gradient(160deg,${G2},${G})`, color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 24, gap: 4 }}>
      <div style={{ width: 84, height: 84, borderRadius: '50%', background: GOLD, color: '#1c2b22', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'how-pop .5s ease-out both', marginBottom: 12 }}><Icon name="check" size={46} /></div>
      <div style={{ fontSize: 21, fontWeight: 700 }}>Төлбөр амжилттай</div>
      <div style={{ fontSize: 13, opacity: .9 }}>А байр, 101 тоот</div>
      <div style={{ fontSize: 28, fontWeight: 800, margin: '6px 0 2px' }}>186 500 ₮</div>
      <div style={{ fontSize: 11, opacity: .85 }}>2026.09.08 · 14:32 · Хаан банк</div>
      <div style={{ width: '100%', display: 'grid', gap: 8, marginTop: 28 }}>
        <div style={{ border: '1.5px solid rgba(255,255,255,.6)', borderRadius: 10, padding: 10, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><Icon name="download" size={14} />Баримт хадгалах</div>
        <div style={{ background: '#fff', color: G, borderRadius: 10, padding: 10, fontSize: 12, fontWeight: 700 }}>Буцах</div>
      </div>
    </div>
  );
}
function QrMock({ size, n = 11 }: { size: number; n?: number }) {
  const cells: number[] = []; let seed = 11;
  for (let k = 0; k < n * n; k++) { seed = (seed * 9301 + 49297) % 233280; cells.push(seed / 233280 > .5 ? 1 : 0); }
  const f = 7; // finder pattern хэмжээ (n=21 үед бодит QR шиг)
  const fs = n >= 21 ? f : 3;
  const finder = (r: number, c: number) => (r < fs && c < fs) || (r < fs && c >= n - fs) || (r >= n - fs && c < fs);
  const finderDark = (r: number, c: number) => {
    const rr = r < fs ? r : r - (n - fs), cc = c < fs ? c : c - (n - fs);
    if (fs === 3) return !(rr === 1 && cc === 1);
    const ring = rr === 0 || cc === 0 || rr === fs - 1 || cc === fs - 1;
    const core = rr >= 2 && rr <= fs - 3 && cc >= 2 && cc <= fs - 3;
    return ring || core;
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${n},1fr)`, gap: n >= 21 ? 0 : 1, width: size, height: size, margin: '0 auto', background: '#fff', padding: 3, boxSizing: 'border-box' }}>
      {cells.map((on, k) => { const r = Math.floor(k / n), c = k % n; const d = finder(r, c) ? finderDark(r, c) : on === 1; return <div key={k} style={{ background: d ? '#1c2b22' : 'transparent' }} />; })}
    </div>
  );
}
